import Vue from 'vue';
import Vuex from 'vuex';
import cloneDeep from 'lodash.clonedeep';
import apiClient from '@/store/api-client';
import timeStore from '@/store/modules/timeStore';
import parametersStore from '@/store/modules/parametersStore';
import errorStore from '@/store/modules/errorStore';
import AvailabilityResponse from '@/models/availabilityResponse';
import { ERROR_CODES } from '@/constants/no-results';
import { PARAM_KEYS } from '@/constants/parameters';

Vue.use(Vuex);

const storeOptions = {
  modules: {
    time: timeStore,
    parameters: parametersStore,
    error: errorStore
  },
  state: {
    baseUrl: null,
    primaryColor: null,
    isFeeExempt: false,
    isLoading: true,
    isAvailable: null,
    unavailableCode: null,
    unavailableDefaultMessage: null,
    unavailableSkus: null,
    productAvailability: null
  },
  mutations: {
    setBaseUrl(state, url) {
      state.baseUrl = url;
    },
    setPrimaryColor(state, color) {
      state.primaryColor = color;
    },
    setIsFeeExempt(state, isFeeExempt) {
      state.isFeeExempt = isFeeExempt;
    },
    setIsAvailable(state, isAvailable) {
      state.isAvailable = isAvailable;
    },
    setIsLoading(state, isLoading) {
      state.isLoading = isLoading;
    },
    setAvailabilityDetails(state, availability) {
      state.isAvailable = availability.isAvailable;
      state.unavailableCode = availability.unavailableCode;
      state.unavailableDefaultMessage = availability.unavailableDefaultMessage;
      state.unavailableSkus = availability.unavailableSkus;
      state.productAvailability = availability.productAvailabilities.reduce(
        (productAvailability, pa) => {
          productAvailability[pa.sku] = {
            isAvailable: pa.isAvailable,
            unavailableCode: pa.unavailableCode,
            unavailableData: pa.unavailableData,
            unavailableDefaultMessage: pa.unavailableDefaultMessage
          };
          return productAvailability;
        },
        {}
      );
    }
  },
  actions: {
    getAvailability({ state, getters, commit }, payload) {
      const zipCode = state.parameters[PARAM_KEYS.ZIP_CODE];
      apiClient(state.baseUrl).availabilityOptions.get(
        state.parameters[PARAM_KEYS.SITE_SLUG],
        getters['parameters/uniqueSkus'],
        zipCode,
        state.parameters[PARAM_KEYS.START_DATE],
        state.parameters[PARAM_KEYS.END_DATE],
        state.parameters[PARAM_KEYS.VARIATION_ID],
        (success) => {
          const availability = new AvailabilityResponse(success.data, zipCode);
          commit('setAvailabilityDetails', availability);
          payload.successCallback(availability);
        },
        (error) => {
          payload.errorCallback(ERROR_CODES.SERVER_ERROR, error.response.data.errors);
        }
      );
    }
  }
};

// This non-standard approach to instantiating the store is a way of giving
// each instance of the app a completely separate store, otherwise, if a page
// uses this Vue app for two cart items on the same page, the second item will
// overwrite the data of the first item. For more information see the following links:
//
// Vuex GitHub Issue: https://github.com/vuejs/vuex/issues/414
// Commit where this was introduced: https://github.com/FromYouFlowers/core/commit/d85cc5e6605f9e6a75b994ae08b83a44b91c7a40
// PR with discussion: https://github.com/FromYouFlowers/core/pull/215/files#r682155911
export function createStore() {
  return new Vuex.Store(cloneDeep(storeOptions));
}